import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import SectionHeading from "@/components/SectionHeading";
import MetricGrid from "@/components/MetricGrid";
import Banner from "@/components/Banner";
import DetailsCard from "@/components/DetailsCard";
import { showSnackbar } from "@/lib/snackbarUtils";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import {
  STAGE_LABELS,
  APPLICATION_TYPE_LABELS,
  UNASSIGNED_REVIEWER_LABEL,
  APPLICATION_REQUIREMENT_METRICS_BY_TYPE
} from "../types";
import type { ApplicationStage, ApplicationType } from "../types";
import { useApplicationContext } from "./useApplicationContext";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "./mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "./queries";
import ApplicationChangeHistory from "./_components/ApplicationChangeHistory";
import ApplicationOrdersSection from "./_components/ApplicationOrdersSection";
import ApplicationTodosSection from "./_components/ApplicationTodosSection";
import AssignReviewerDialog, { type ReviewerOption } from "./_components/AssignReviewerDialog";
import MembershipHistoryTable from "@/features/person/components/MembershipHistoryTable";
import { GET_PERSON_DUES_BALANCE_QUERY, PERSON_BANDS_QUERY } from "@/pages/people/[pgaId]/person/queries";
import type { Band } from "@/types";
import { buildRequirementMetrics, REQUIREMENT_METRICS_GRID_COLS } from "./_utils/applicationRequirementMetrics";
import {
  buildAcceptableProgressOverviewOptions,
  isAssociateReapplyApplication,
  isDraftApplicationStatus
} from "./utils";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";

export default function ApplicationOverviewPage() {
  const navigate = useNavigate();
  const { application } = useApplicationContext();
  const currentUser = useCurrentBackofficeUser();
  const { setError, clearError } = useErrorBanner();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const applicationId = application?.id ?? "";
  const pgaId = application?.person.pgaId ?? "";
  const appType = application?.applicationType as ApplicationType | undefined;
  const showDuesRequirement = appType ? APPLICATION_REQUIREMENT_METRICS_BY_TYPE[appType].includes("dues") : false;
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });

  const needsDuesBalanceQuery = showDuesRequirement && canViewPersonBilling && appType === "member";

  const { data: duesBalanceData, loading: duesBalanceLoading } = useQuery(GET_PERSON_DUES_BALANCE_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !needsDuesBalanceQuery
  });

  const {
    data: membershipData,
    loading: membershipLoading,
    error: membershipError
  } = useQuery<{ person: { bands: Band[] } }>(PERSON_BANDS_QUERY, {
    variables: { pgaId },
    skip: !pgaId
  });

  const [updateApplication, { loading: savingReviewer }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data) => {
      if (data.updateSubmittedApplication.success) {
        clearError();
        setAssignDialogOpen(false);
        showSnackbar(data.updateSubmittedApplication.message);
      } else {
        setError(data.updateSubmittedApplication.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  if (!application) return null;

  const status = application.status as ApplicationStage | undefined;
  const isDraft = isDraftApplicationStatus(status ?? "");
  const showProcessActions = !isDraft && status !== "approved" && status !== "denied";
  const requirementMetrics = buildRequirementMetrics(pgaId, {
    applicationType: appType,
    experienceCredits: application.experienceCredits,
    backgroundCheck: application.person.prospectProgram?.backgroundCheck ?? null,
    qualifyingLevel: application.person.prospectProgram?.qualifyingLevel ?? null,
    pgmProgressStatus: application.person.pgmProgressStatus ?? null,
    playingAbilityTestStatus: application.person.prospectProgram?.playingAbilityTestStatus ?? null,
    duesBalance: needsDuesBalanceQuery ? (duesBalanceData?.person?.duesBalance ?? null) : null,
    associateApplicationEligibility: application.associateApplicationEligibility,
    affiliateApplicationEligibility: application.affiliateApplicationEligibility,
    memberApplicationEligibility: application.memberApplicationEligibility
  }).filter((metric) => {
    if (metric.label !== "Dues Status (Balance)") return true;
    if (!showDuesRequirement || !canViewPersonBilling || duesBalanceLoading) return false;
    return true;
  });
  const reviewerName = application.reviewer?.name ?? UNASSIGNED_REVIEWER_LABEL;
  const reviewerOption: ReviewerOption | null = application.reviewer
    ? {
        id: application.reviewer.id,
        name: application.reviewer.name,
        email: application.reviewer.email ?? ""
      }
    : null;
  const acceptableProgressOptions = isAssociateReapplyApplication(application)
    ? buildAcceptableProgressOverviewOptions(application.person.pgmProgressStatus?.programExpirationDate)
    : [];

  const handleAssignReviewer = async (reviewerId: string) => {
    if (savingReviewer || !applicationId || !reviewerId) return;

    await updateApplication({
      variables: {
        input: {
          id: applicationId,
          reviewerId
        }
      }
    });
  };

  let reviewerBannerDescription: string;
  if (!application.reviewer) {
    reviewerBannerDescription = "A reviewer must be assigned to this application before it can be processed.";
  } else if (currentUser?.id === application.reviewer.id) {
    reviewerBannerDescription = "You are currently assigned as the reviewer for this application.";
  } else {
    reviewerBannerDescription = `Reviewer assigned to ${reviewerName}. Reassign if needed.`;
  }

  return (
    <>
      <AssignReviewerDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        currentReviewer={reviewerOption}
        saving={savingReviewer}
        onAssign={(reviewer) => void handleAssignReviewer(reviewer.id)}
      />
      <ApplicationOrdersSection
        registrationOrder={application.registrationOrder}
        upcomingDuesOrder={application.upcomingDuesOrder}
      />
      {showProcessActions && (
        <Banner
          variant={application.reviewer ? "info" : "warn"}
          description={reviewerBannerDescription}
          hideSecondaryDismiss
          primaryAction={
            currentUser && currentUser.id !== application.reviewer?.id
              ? {
                  label: "Assign myself",
                  onClick: () => void handleAssignReviewer(currentUser.id)
                }
              : undefined
          }
          secondaryAction={{
            label: "Assign Another Person",
            onClick: () => setAssignDialogOpen(true)
          }}
          className="mt-8"
        />
      )}
      {!isDraft && <ApplicationTodosSection application={application} />}
      {showProcessActions && (
        <>
          <SectionHeading
            heading="Additional Application Requirements"
            body="View the requirements for this application required for approval that have been automatically verified"
          />
          <div className={REQUIREMENT_METRICS_GRID_COLS[requirementMetrics.length] ?? ""}>
            <MetricGrid metrics={requirementMetrics} layout="compact" hasHeading />
          </div>
        </>
      )}
      {membershipError ? (
        <ErrorBoundary variant="inline" title="Failed to Load Membership History" message={membershipError.message} />
      ) : (
        <MembershipHistoryTable
          bands={membershipData?.person.bands ?? []}
          loading={membershipLoading}
          pgaId={pgaId}
          refetchQueries={[PERSON_BANDS_QUERY]}
          headingActions={[
            {
              label: "View Full History",
              variant: "outlined",
              onClick: () => navigate(`/people/${pgaId}/membership/overview`),
              testId: "view-full-membership-history-button"
            }
          ]}
        />
      )}
      <SectionHeading heading="Application Summary" body="View the details of the application" />
      <div className="max-w-lg">
        <DetailsCard
          heading="Details"
          options={[
            {
              iconName: "badge",
              overline: "Application Type",
              headline: appType ? (APPLICATION_TYPE_LABELS[appType] ?? appType) : "--"
            },
            ...acceptableProgressOptions,
            {
              iconName: "fact_check",
              overline: "Stage",
              headline: status ? (STAGE_LABELS[status] ?? status) : "--"
            },
            {
              iconName: "event",
              overline: "Submitted Date",
              headline: application.submittedAt ? (toDateTimeFormat(application.submittedAt) ?? "--") : "--"
            },
            {
              iconName: "domain",
              overline: "Section",
              headline: application.person.primarySection?.name ?? "--"
            },
            {
              iconName: "how_to_reg",
              overline: "Reviewer",
              headline: reviewerName
            }
          ]}
        />
      </div>
      <SectionHeading
        heading="Change History"
        body="Track updates to this application, including status changes and staff eligibility reviews."
      />
      <ApplicationChangeHistory changelogs={application.changelogs ?? []} />
    </>
  );
}
